import * as React from 'react';
import { ElementLoader, PromptConfirmation, RadioSymbol } from 'ui';
import { ModalWrapper } from 'app/components/modal-wrapper/modal-wrapper';
import { SelectMultiple } from 'app/area/shared/translated-components';
import { ComplexValue, LoadOptions } from 'app/types-shared';
import { endpoints } from 'app/area/favourite-administration/form/endpoints';
import { FavouriteListIds } from 'app/area/favourite-administration/list/constants';
import { ChangeOptionType } from 'app/area/favourite-administration/list/types';
import { ChangeOwnerComponentProps, ChangeOwnerField } from 'app/area/favourite-administration/list/change-owner/types';
import { allFieldsEmpty, hasFieldValueChanged, isDisabled } from 'app/area/favourite-administration/list/change-owner/change-owner-helpers';
import { Owner } from 'app/area/favourite-administration/list/change-owner/owner';
import { ReplaceWithOwners } from 'app/area/favourite-administration/list/change-owner/replace-with-owners';
import { ApplyOnDescendants } from 'app/area/favourite-administration/list/components/apply-on-descendants';
import styles from './change-owner.scss';

interface ChangeOwnerComponentState {
  shouldDisplayModal: boolean;
  mode: ChangeOptionType;
  selectedOwners: ComplexValue<string | number>[];
}

export class ChangeOwnerModal extends React.PureComponent<ChangeOwnerComponentProps, ChangeOwnerComponentState> {

  constructor(props: ChangeOwnerComponentProps) {
    super(props);

    this.state = {
      shouldDisplayModal: undefined,
      mode: ChangeOptionType.Add,
      selectedOwners: []
    };
  }

  public componentDidUpdate(prevProps: ChangeOwnerComponentProps) {
    this.handleIsDirty(prevProps);
  }

  private handleIsDirty = (prevProps: ChangeOwnerComponentProps) => {
    hasFieldValueChanged(prevProps, this.props) && this.props.updateIsDirty(true);
  };

  private toggleModal = () => {
    this.setState({ shouldDisplayModal: !this.state.shouldDisplayModal });
  };

  private continue = () => {
    this.toggleModal();

    const selectedOwners = this.state.selectedOwners.map(owner => Number(owner.value));
    this.props.save(this.state.mode, selectedOwners);

    this.props.updateIsDirty(false);
  };

  private onClose = () => {
    this.props.onClose();
    this.props.updateIsDirty(false);
  };

  private onApplyOnDescendants = (value: number) => {
    this.props.updateByKey(ChangeOwnerField.applyOnDescendants, !!value);
  };

  private clearOwnerForms = () => {
    this.props.updateByKey(ChangeOwnerField.fromOwners, undefined);
    this.props.updateByKey(ChangeOwnerField.toOwners, undefined);
    this.props.resetValidation(ChangeOwnerField.fromOwners);
    this.props.resetValidation(ChangeOwnerField.toOwners);
    this.props.resetOwnersValidation();
  };

  private selectReplaceOwners = () => {
    this.clearOwnerForms();
    this.setState({ mode: ChangeOptionType.Replace, selectedOwners: [] });
  };

  private selectAddOwners = () => {
    this.clearOwnerForms();
    this.setState({ mode: ChangeOptionType.Add, selectedOwners: [] });
  };

  private onSelectedOwnersChange = (value: ComplexValue<string | number>[]) => {
    this.props.resetOwnersValidation();
    this.setState({ selectedOwners: value });
  };

  private onLoadOwnerOptions = (loadOptionsParams: LoadOptions) => {
    this.props.loadOptions(FavouriteListIds.toOwners, endpoints.ownerOptions, loadOptionsParams);
  };

  private isSaveDisabled = (isAddMode: boolean, fetchingChangeOwner: boolean): boolean => {
    if (fetchingChangeOwner) {
      return true;
    }

    return isAddMode
      ? this.state.selectedOwners.length === 0
      : allFieldsEmpty(this.props) || isDisabled(this.props);
  };

  private renderOwnersMultiSelectOption = (option: {
    id: string;
    mode: ChangeOptionType;
    isChecked: boolean;
    labelText: string;
    onSelect: () => void;
  }) => {
    const { id, mode, isChecked, labelText, onSelect } = option;
    const { options, validation, locale, translation } = this.props;
    const ownerOptions = options?.[FavouriteListIds.toOwners];

    return <>
      <input
        id={id}
        type="radio"
        className="form-check-input visuallyhidden"
        value={mode}
        checked={isChecked}
        onChange={onSelect} />
      <label
        htmlFor={id}
        className="form-check-label">
        <RadioSymbol />
        <b>{labelText}</b>
      </label>
      {isChecked && <div className={styles.section}>
        <SelectMultiple
          componentId={FavouriteListIds.toOwners}
          className="w-100"
          valueItems={this.state.selectedOwners}
          label={{ text: translation.owners, visible: true, required: true }}
          options={(ownerOptions?.codes as ComplexValue<string | number>[]) ?? []}
          placeholder={translation.search}
          fetching={ownerOptions?.fetching}
          onLoadOptions={this.onLoadOwnerOptions}
          onChange={this.onSelectedOwnersChange}
          validation={validation?.[FavouriteListIds.owners]}
          locale={locale} />
      </div>}
    </>;
  };

  private renderReplaceOwnersOption = (isReplaceMode: boolean) => {
    const { translation } = this.props;

    return <>
      <input
        id="replace-owners"
        type="radio"
        className="form-check-input visuallyhidden"
        value={ChangeOptionType.Replace}
        checked={isReplaceMode}
        onChange={this.selectReplaceOwners} />
      <label
        htmlFor="replace-owners"
        className="form-check-label">
        <RadioSymbol />
        <b>{translation.replace_owners}</b>
      </label>
      {isReplaceMode && <div className={styles.section}>
        <Owner {...this.props} />
        <ReplaceWithOwners {...this.props} />
      </div>}
    </>;
  };

  private renderModalBody = (isAddMode: boolean, isReplaceMode: boolean, form: { [key: string]: any }) => {
    const { translation } = this.props;

    return <div>
      <p className={`${styles.changeOwnerText} mb-3`}>
        {translation.change_owner_description}
      </p>
      <div className="row">
        <div className="col-12">
          <div className={styles.container}>
            <div className="form-check">
              {this.renderOwnersMultiSelectOption({
                id: 'add-owners', mode: ChangeOptionType.Add, isChecked: isAddMode,
                labelText: translation.add_owners, onSelect: this.selectAddOwners
              })}
              {this.renderReplaceOwnersOption(isReplaceMode)}
            </div>
          </div>
          <ApplyOnDescendants
            value={form?.[ChangeOwnerField.applyOnDescendants]}
            onChange={this.onApplyOnDescendants}
            translation={translation} />
        </div>
      </div>
    </div>;
  };

  private renderConfirmation = () => {
    const { translation } = this.props;

    return <PromptConfirmation
      title={translation.change_owner}
      description={translation.change_owner_confirmation_description}
      continueText={translation.change_owner}
      cancelText={translation.cancel}
      onContinue={this.continue}
      onCancel={this.toggleModal}
    />;
  };

  public render() {
    const { list: { changeOwner: { form } = {}, numberOfResults, fetchingChangeOwner }, translation } = this.props;

    const isReplaceMode = this.state.mode === ChangeOptionType.Replace;
    const isAddMode = this.state.mode === ChangeOptionType.Add;

    return (<>
      <ModalWrapper
        headerTitle={`${translation.change_owner} (${numberOfResults})`}
        btnTitle={translation.change_owner}
        btnCancelTitle={translation.cancel}
        onClose={this.onClose}
        onDone={this.toggleModal}
        closeOnDone={false}
        isCancelDisabled={fetchingChangeOwner}
        isDisabled={this.isSaveDisabled(isAddMode, fetchingChangeOwner)}
        canCloseOnBackground={!fetchingChangeOwner}
        canCloseOnXButton={!fetchingChangeOwner}>

        {fetchingChangeOwner ? <ElementLoader /> : this.renderModalBody(isAddMode, isReplaceMode, form)}
      </ModalWrapper>

      {this.state.shouldDisplayModal && this.renderConfirmation()}
    </>);
  }
}
